import { Button, Flex, Loader, Select, SelectProps, Text } from "@mantine/core";
import { forwardRef, lazy, useCallback, useEffect, useMemo, useState } from "react";

import {
  ItemOption,
  useItemsAutoComplete,
  useItemsCode,
} from "@/custom/features/ui/autoComplete/item/auto-useGetItemsQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import styled from "@emotion/styled";

import { AbstractItemAutoComplete } from "@/features/ui/autoComplete/abstractItem/abstractItem-autoComplete";
import { GradesNameAutoComplete } from "@/features/ui/autoComplete/grade/grades-autoComplete-name";
import { ItemCategoryAutoComplete } from "@/features/ui/autoComplete/itemCategory/itemCategory-autoComplete";
import { ItemTypesMultiAutoComplete } from "@/features/ui/multiAutoComplete/itemType/itemType-multiAutoComplete";
import { useForm } from "@mantine/form";
import { ItemsGet200ResponseRowsInnerItemTypeEnum } from "@sizlcorp/sizl-api-document/dist/models";
import { IconX } from "@tabler/icons-react";
import { debounce } from "es-toolkit";

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  group?: string;
  label: string;
  value: string;
  spec?: string;
}

export interface ItemAutoCompleteProps
  extends Partial<SelectProps>, Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (itemCode: string | null) => void;
  onChangeItem?: (item: ItemOption) => void;
  maxDropdownHeight?: number;
  width?: string;
  getItemType?: ItemsGet200ResponseRowsInnerItemTypeEnum[];
  getGrade?: string;
  getCategory?: string;
  getAbstractItem?: string;
}

// 입력값: value (item 모델의 code)
// 출력값: onChange (item 모델의 code)

const ItemsCreateForm = lazy(() =>
  import("@/features/item/components/form/ItemsCreateForm").then((module) => ({
    default: module.ItemsCreateForm,
  })),
);

export const ItemAutoComplete = (params: ItemAutoCompleteProps) => {
  const {
    value: itemCode,
    onChange,
    onChangeItem,
    maxDropdownHeight,
    width,
    getItemType,
    getGrade,
    getCategory,
    getAbstractItem,
    ...etcParams
  } = params;

  const form = useForm({
    initialValues: {
      itemType: getItemType && getItemType.length ? getItemType : [],
      gradeName: getGrade ?? "",
      itemCategoryCode: getCategory ?? "",
      abstractItemCode: getAbstractItem ?? "",
    },
  });

  // Lazy loading: 드롭다운이 한 번이라도 열리면 그때부터 데이터 로드
  const [hasBeenOpened, setHasBeenOpened] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [debouncedKeyword, setDebouncedKeyword] = useState<string>("");
  const [debouncedFormValues, setDebouncedFormValues] = useState(form.values);

  // 드롭다운이 한 번이라도 열렸거나 현재 열려있을 때만 API 호출
  const shouldLoadOptions = hasBeenOpened || focused;

  const { data: options, isLoading: optionLoading } = useItemsAutoComplete(
    shouldLoadOptions,
    debouncedKeyword,
    debouncedFormValues,
    getItemType,
  );

  // 초기값(value 또는 defaultValue)이 있을 때만 로드
  const { data: initialOptions, isLoading } = useItemsCode(!!itemCode, itemCode ?? null);

  // selectOptions 메모이제이션으로 불필요한 재계산 방지
  const selectOptions = useMemo(() => {
    return [...(options ?? []), ...(initialOptions ?? [])].reduce(
      (unique: ItemProps[], option: ItemProps) => {
        return unique.some((u) => u.value === option.value) ? unique : [...unique, option];
      },
      [],
    );
  }, [options, initialOptions]);

  const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ group, label: name, value: code, ...others }, ref) => {
      return (
        <div ref={ref} {...others}>
          <Flex direction="row" justify="space-between" align="center">
            <Flex direction={"column"}>
              <Text>{name}</Text>
              {/* <Text size={"xs"} color="#666666"> */}
              {/* Grade: {grade}, Color: {color}, 형태: {shape} */}
              {/* </Text> */}
            </Flex>
            <Text fz="xs">{group}</Text>
          </Flex>
        </div>
      );
    },
  );

  const selectedItem = useMemo(() => {
    return initialOptions?.find((item) => item.value === itemCode);
  }, [initialOptions, itemCode]);

  const onChangeHandler = useCallback(
    (e: string | null) => {
      const selected = options?.find((item) => item.value === e);
      onChange?.(e);
      onChangeItem?.(selected as ItemOption);
    },
    [options, onChange, onChangeItem],
  );

  // 검색어 debounce
  const debouncedSearch = useCallback(
    debounce((keyword: string) => {
      setDebouncedKeyword(keyword);
    }, 300), // 300ms로 단축하여 반응성 개선
    [],
  );

  // form.values debounce - 필터 변경 시 API 호출 최소화
  const debouncedFormUpdate = useCallback(
    debounce((values: typeof form.values) => {
      setDebouncedFormValues(values);
    }, 500), // 500ms 지연
    [],
  );

  // searchKeyword 변경 시 debounce
  useEffect(() => {
    if (shouldLoadOptions) {
      debouncedSearch(searchKeyword);
    }
  }, [searchKeyword, shouldLoadOptions, debouncedSearch]);

  // form.values 변경 시 debounce (드롭다운이 한 번이라도 열렸을 때만)
  useEffect(() => {
    if (shouldLoadOptions) {
      debouncedFormUpdate(form.values);
    }
  }, [form.values, shouldLoadOptions, debouncedFormUpdate]);

  // 드롭다운이 열릴 때 lazy loading 활성화
  const handleDropdownOpen = useCallback(() => {
    setFocused(true);
    setHasBeenOpened(true);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setFocused(false);
  }, []);

  return (
    <Flex direction="column" w="100%" gap="sm">
      <ItemSelect
        withinPortal
        onDropdownOpen={handleDropdownOpen}
        onDropdownClose={handleDropdownClose}
        clearable
        value={itemCode}
        itemComponent={SelectItem}
        searchValue={searchKeyword}
        data={selectOptions}
        searchable
        maxDropdownHeight={maxDropdownHeight ?? 150}
        onChange={onChangeHandler}
        onSearchChange={setSearchKeyword}
        rightSection={ItemInfo({
          item: selectedItem as ItemOption,
          onChange: onChangeHandler,
        })}
        filter={(value, item) =>
          item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
          item?.value?.toLowerCase().includes(value.toLowerCase().trim())
        }
        nothingFound={AddNewItem({
          itemName: searchKeyword,
          onChange: onChangeHandler,
          isLoading: isLoading,
          optionLoading: optionLoading,
        })}
        {...etcParams}
      />
      <Flex w="100%" gap="sm">
        <ItemTypesMultiAutoComplete
          w="50%"
          label="품목군"
          disabled={getItemType && getItemType?.length > 0}
          value={form.values.itemType}
          {...form.getInputProps("itemType")}
        />
        <GradesNameAutoComplete w="50%" label="Grade" {...form.getInputProps("gradeName")} />
      </Flex>
      <Flex w="100%" gap="sm">
        <ItemCategoryAutoComplete
          w="50%"
          label="Color"
          {...form.getInputProps("itemCategoryCode")}
        />
        <AbstractItemAutoComplete
          w="50%"
          label="형태"
          {...form.getInputProps("abstractItemCode")}
        />
      </Flex>
    </Flex>
  );
};

const ItemInfo = (params: { item?: ItemOption; onChange: (itemCode: string | null) => void }) => {
  const { item, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return item?.value ? (
    <ItemInfoLabel>
      {/* ({item.value}) */}
      <IconX size="1rem" onClick={clearHandler} />
    </ItemInfoLabel>
  ) : null;
};

const AddNewItem = (params: {
  itemName: string;
  onChange: (itemCode: string) => void;
  isLoading?: boolean;
  optionLoading?: boolean;
}) => {
  const { itemName, onChange, isLoading, optionLoading } = params;
  const { openModal } = useModal();

  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(<ItemsCreateForm />, null, "신규 품목 등록", true).then((result) => {
      onChange(result?.code);
    });
  };

  return (
    <Button variant="subtle" onClick={(e) => !!itemName && addNewModalHandler(e)}>
      {isLoading || optionLoading ? (
        <div
          style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
        >
          <Flex justify="center" align="center" gap="sm">
            <Text>Not Found</Text>
            <Loader size="sm" />
          </Flex>
        </div>
      ) : (
        `신규 품목(${itemName}) 등록`
      )}
    </Button>
  );
};
const ItemInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
  align-items: center;
  // right: 30px;
  position: absolute;
`;

const ItemSelect = styled(Select)<{ value: string | null | undefined }>`
  .mantine-Select-input {
    font-size: 0.8rem;
    // padding-bottom: ${(props) => (props.value ? "0.8rem !important" : "0.4rem !important")};

    &.item-dirty {
      color: cadetblue !important;
      border-color: cadetblue;
    }
  }
  .mantine-Select-error {
    font-size: 11px;
    color: red;
  }

  /* 드롭다운 높이 고정 및 부드러운 전환 */
  .mantine-Select-dropdown {
    min-height: 150px !important;
    max-height: 150px !important;
    transition: height 0.2s ease-out, opacity 0.15s ease-out !important;
  }

  .mantine-Select-itemsWrapper {
    max-height: 150px !important;
    transition: max-height 0.2s ease-out !important;
  }
`;
